import { useEffect, useRef, useState, useCallback } from "react";
import "./messagingBody.css";
import { util } from "../helpers/common";
import { CONVERSATION_CONSTANTS } from "../helpers/constants";
import { t } from "../awac/i18n";

// Import children components to plug in and render.
import ConversationEntry from "./conversationEntry";
import TypingIndicator from "./typingIndicator";

// Distance (px) from the bottom within which we treat the user as "pinned"
// to the latest message — beyond this we stop auto-scrolling and surface the
// scroll-to-bottom button instead.
const SCROLL_PIN_THRESHOLD = 64;

export default function MessagingBody({ conversationEntries, conversationStatus, typingParticipants, showTypingIndicator, onAwacAction, onAwacRender }) {

    const scrollRef = useRef(null);
    const bottomRef = useRef(null);
    // Whether the user is currently pinned to the bottom of the transcript.
    const pinnedRef = useRef(true);
    const [showScrollToBottom, setShowScrollToBottom] = useState(false);

    useEffect(() => {
        if (conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION) {
            // Render conversation closed message.

            // Remove typing indicator.
            typingParticipants = [];
            showTypingIndicator = false;
        }
    }, [conversationStatus]);

    const scrollToBottom = useCallback((behavior = "smooth") => {
        const node = scrollRef.current;
        if (!node) {
            return;
        }
        node.scrollTo({ top: node.scrollHeight, behavior });
        pinnedRef.current = true;
        setShowScrollToBottom(false);
    }, []);

    // Track whether the user has scrolled away from the bottom.
    const handleScroll = useCallback(() => {
        const node = scrollRef.current;
        if (!node) {
            return;
        }
        const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
        const atBottom = distanceFromBottom <= SCROLL_PIN_THRESHOLD;
        pinnedRef.current = atBottom;
        setShowScrollToBottom(!atBottom);
    }, []);

    // Smooth autoscroll whenever new entries arrive or the typing indicator
    // toggles. Two cases:
    //  - The VISITOR's own message: always scroll and RE-PIN, even if they had
    //    drifted up reading history — pressing Enter is an explicit "follow the
    //    conversation", and the reply must land on-screen (bug: replies were
    //    arriving off-screen after a send while >64px from the bottom).
    //  - Agent/system entries: scroll only while pinned, so we never yank the
    //    visitor away from older messages they're deliberately reading.
    useEffect(() => {
        const last = conversationEntries.length
            ? conversationEntries[conversationEntries.length - 1]
            : null;
        if (last && last.isEndUserMessage) {
            scrollToBottom("smooth");
        } else if (pinnedRef.current) {
            scrollToBottom("smooth");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationEntries.length, showTypingIndicator, scrollToBottom]);

    // Late-growing content (unit summaries, photos, staggered reveals) makes a
    // smooth scroll fired at entry-time land SHORT of the bottom — which then
    // reads as "not pinned" and kills the next autoscroll. While pinned, track
    // content growth and keep the bottom stuck to the bottom.
    useEffect(() => {
        const node = scrollRef.current;
        if (!node || typeof ResizeObserver === "undefined") {
            return undefined;
        }
        const list = node.querySelector(".conversationEntriesListView");
        if (!list) {
            return undefined;
        }
        const ro = new ResizeObserver(() => {
            if (pinnedRef.current) {
                node.scrollTo({ top: node.scrollHeight, behavior: "auto" });
            }
        });
        ro.observe(list);
        return () => ro.disconnect();
    }, []);

    /**
     * Builds a list of conversation entries where each conversation-entry represents an object of type defined in constants#CONVERSATION_CONSTANTS.EntryTypes.
     * @returns {string}
     */
    const conversationEntriesListView = conversationEntries.map(conversationEntry =>
        <ConversationEntry
            key={conversationEntry.messageId} 
            conversationEntry={conversationEntry}
            onAwacAction={onAwacAction}
            onAwacRender={onAwacRender} />
    );

    /**
     * Generates a text with conversation start date and time.
     * @returns {string}
     */
    function generateConversationStartTimeText() {
        if (conversationEntries.length) {
            const conversationStartTimestamp = conversationEntries[0].transcriptedTimestamp;
            const startDate = util.getFormattedDate(conversationStartTimestamp);
            const startTime = util.getFormattedTime(conversationStartTimestamp);
            const conversationStartTimeText = t("chat.started", { date: startDate, time: startTime });
            return conversationStartTimeText;
        }
        return "";
    }

    /**
     * Generates a text with conversation end date and time.
     * @returns {string}
     */
    function generateConversationEndTimeText() {
        const conversationEndTimestamp = Date.now();
        const endDate = util.getFormattedDate(conversationEndTimestamp);
        const endTime = util.getFormattedTime(conversationEndTimestamp);
        const conversationEndTimeText = t("chat.ended", { date: endDate, time: endTime });

        return conversationEndTimeText;
    }

    return (
        <div className="messagingBodyWrapper">
            <div className="messagingBody" ref={scrollRef} onScroll={handleScroll}>
                {conversationEntries.length > 0 && <p className="conversationStartTimeText">{generateConversationStartTimeText()}</p>}
                <ul
                    className="conversationEntriesListView"
                    role="log"
                    aria-live="polite"
                    aria-relevant="additions"
                    aria-label={t("chat.transcriptLabel")}>
                    {conversationEntriesListView}
                </ul>
                {showTypingIndicator && <TypingIndicator typingParticipants={typingParticipants}/>}
                {conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION && <p className="conversationEndTimeText">{generateConversationEndTimeText()}</p>}
                <div ref={bottomRef} aria-hidden="true" />
            </div>
            <button
                type="button"
                className={"scrollToBottomButton" + (showScrollToBottom ? " scrollToBottomButtonVisible" : "")}
                aria-label={t("chat.scrollLatest")}
                title={t("chat.scrollLatest")}
                tabIndex={showScrollToBottom ? 0 : -1}
                onClick={() => scrollToBottom("smooth")}>
                <svg className="scrollToBottomIcon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 16.5a1 1 0 0 1-.71-.29l-6-6a1 1 0 0 1 1.42-1.42L12 14.09l5.29-5.3a1 1 0 0 1 1.42 1.42l-6 6a1 1 0 0 1-.71.29z"/>
                </svg>
            </button>
        </div>
    );
}
